import '../styles/Reunion.css';
import React, {useState, useEffect, useRef, useLayoutEffect} from 'react';
import {useParams, Link, useNavigate} from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {ImgZoom, Loading, useAuthUser, MediaImageLoading, MediaPDF} from '../components/components_index';
import { usePeople, useReunions, useImgDescs, useMD, getImgOptions } from '../hooks/hooks_index';

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

export default function Reunion({children}) {
  const {reunionID} = useParams();

  return (
    <Page reunionID={reunionID}>
      <Title />
      <MainInfo />
      <Gallery />
    </Page>
  );
}

function Page({children, reunionID}) {
  const {data: people, isLoading: peopleLoading, error: peopleError} = usePeople();
  const {data: reunions, reunionsLoading, reunionsError} = useReunions();
  const {data: image_infos, imgLoading, imgError} = useImgDescs();
  const { role } = useAuthUser();

  // if (!reunions) return <p>Loading...</p>;
  if (!reunions || !people || reunionsLoading || imgLoading) return <Loading/>;
  if (reunionsError || peopleError|| imgError) return <div>Error loading</div>;

  const reunion = reunions[reunionID];

  const childrenWithProps = React.Children.map(children, child =>
    React.cloneElement(child, {reunion, image_infos, reunionID, people, reunions})
  );

  return (
    <div className='reunion-background'>
      {childrenWithProps}
      {(role === "admin") &&
        <Link to={`/edit-reunion-bio/${reunionID}`}>
          Edit This Reunion Page
        </Link>
      }
    </div>
  );
}

function Title({reunion, reunions}) {
  const navigate = useNavigate();
  const nextID = `R${Number(reunion.year) + 1}`;
  const prevID = `R${Number(reunion.year) - 1}`;
  const isNext = reunions[nextID] ?? false;
  const isPrev = reunions[prevID] ?? false;

  function prevReunion () {
    if (isPrev) {
      navigate(`/reunion/${prevID}`)
    }
  }

  function nextReunion () {
    if (isNext) {
      navigate(`/reunion/${nextID}`)
    }
  }

  return (
    <div className='title-box-reunion'>
      <button className={`reunion-scroll prev-reunion ${isPrev ? "":" no-reunion"}`} onClick={prevReunion} disabled={!isPrev} title="Previous Year's Reunion">
        {'\u2039'}
      </button>
      <div className='title-text-reunion'>
        {reunion.year} Spradley Family Reunion
      </div>
      <button className={`reunion-scroll next-reunion ${isNext ? "":" no-reunion"}`} onClick={nextReunion} disabled={!isNext} title="Next Year's Reunion">
        {'\u203A'}
      </button>
    </div>
  );
}

function MainInfo({reunion, reunionID, image_infos, people}) {
  const reunionMainImg = reunion.main_pic || "S_logo_PNG.png";
  const {data: reunionText, isLoading} = useMD("reunion", reunionID)

  const reunionSections = parseReunionBio(reunionText);

  const startMonth = MONTHS[reunion.start_date.s_month -1];
  const endMonth = MONTHS[reunion.end_date.e_month -1];
  const reunionAddress = reunion.address.formattedAddress
    .replace(/\b(undefined|null)\b/g, "")
    .split(",")
    .map(part => part.trim())
    .filter(Boolean)
    .join(", ")
  const reunionAttendees = reunion.people

  if (isLoading) return <Loading/>

  return (
    <div className='main-info-box'>
      <MediaImageLoading key={reunion.year} src={reunionMainImg} className={'reunion-main-img'} containerName={'reunion'}/>
      {reunion.reunion_num &&
        <div className='reunion-info-row number-row'>
          <span className='row-title'>{getNumberWithSuffix(reunion.reunion_num)} Spradley Family Reunion</span>
        </div>
      }
      <div className='reunion-info-row date-row'>
        <span className='row-title'>Dates:</span>
        <span className='row-value'>{startMonth} {reunion.start_date.s_day}</span>
        <span className='row-value'>–</span>
        <span className='row-value'>{endMonth} {reunion.end_date.e_day},</span>
        <span className='row-value'>{reunion.year}</span>
      </div>
      <div className='reunion-info-row location-row'>
        <span className='row-title'>Location:</span>
        <span className='row-value'>{reunionAddress}</span>
      </div>
      {reunionAttendees &&
        <div className='reunion-info-row people-row'>
          <div className='row-list'>
            <span className='row-title attendees-title'>Attendees ({reunionAttendees.length} total):</span>
            {[...reunionAttendees]
              .sort((a, b) => {
                const dateA = people[a].b_details;
                const dateB = people[b].b_details;

                if (!dateA && !dateB) return 0;
                if (!dateA) return 1;
                if (!dateB) return -1;

                return (
                  dateA.b_year - dateB.b_year ||
                  dateA.b_month - dateB.b_month ||
                  dateA.b_day - dateB.b_day
                );
              })
              .map((person, index) => (
                <span className='row-value' key={person}>
                  <Link to={`/people/${person}`}>
                    {people[person].AKA || `${people[person].f_name} ${people[person].l_name}`}
                  </Link>
                  {index !== reunionAttendees.length -1 && <span>,</span>}
                </span>
              ))}
              <span className='row-value'>(oldest to youngest)</span>
          </div>
        </div>
      }
      <div className='reunion-info-row overview-row'>
        <span className='row-title'>Overview:</span>
        <MarkdownContent image_infos={image_infos}>
          {reunionSections.overview}
        </MarkdownContent>
      </div>
      <div className='reunion-info-row bests-row'>
        <span className='row-title'>Favorite Moments:</span>
        <MarkdownContent image_infos={image_infos}>
          {reunionSections.bestMoments}
        </MarkdownContent>
      </div>
      <div className='reunion-info-row auction-row'>
        <span className='row-title'>Auction:</span>
        <MarkdownContent image_infos={image_infos}>
          {reunionSections.auction}
        </MarkdownContent>
      </div>
      <div className='reunion-info-row files-row'>
        <span className='row-title files-title'>Files:</span>
        <div className='pdf-list'>
          {reunion.reunion_pdfs && reunion.reunion_pdfs.map(pdf => (
            <MediaPDF pdfID={pdf.pdf_link} name={pdf.pdf_title}/>
          ))}
          {!reunion.reunion_pdfs &&
            <div className='no-files'>No uploaded files for this reunion</div>
          }
          {/* <MediaPDF pdfID="1902 Reunion Treasurer's Report.pdf" name="1902 Treasurer's Report"/>
          <MediaPDF pdfID="1902 Reunion Treasurer's Report.pdf" name="1902 Treasurer's Report"/> */}
        </div>
      </div>
    </div>
  )
}

function Gallery({person, image_infos, reunionID, people}) {
  const [images, setImages] = useState([]);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const [showArrows, setShowArrows] = useState(false);
  const [atLeft, setAtLeft] = useState(true);
  const [atRight, setAtRight] = useState(false);
  
  const scrollAmount = 200;

  const scrollLeft = () => smoothScrollBy(scrollRef.current, -scrollAmount, 400)
  const scrollRight = () => smoothScrollBy(scrollRef.current, scrollAmount, 400)

  useEffect(() => {
    getImgOptions({
      reunion: reunionID,
      sortByDate: ["saved_date", "desc"]
    })
      .then(data => setImages(data))
      .catch(err => setError(err.message));
  }, [reunionID]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const checkOverflow = () => {
      setShowArrows(el.scrollWidth > el.clientWidth);
    };
    checkOverflow();

    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [images]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const updateScrollState = () => {
      const overflowing = el.scrollWidth > el.clientWidth;

      setShowArrows(overflowing);

      setAtLeft(el.scrollLeft <= 0);

      setAtRight(
        el.scrollLeft + el.clientWidth >= el.scrollWidth - 1
      );
    };

    updateScrollState();

    el.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);

    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [images]);

  if (error) return <div>Error: {error}</div>

  return (
    <div className='reunion-gallery'>
      <h2>Gallery</h2>
      <div className='gallery-wrapper'>
        <div className='gallery-images-box' ref={scrollRef}>
          {images.map(img => (
            <div className='reunion-gallery-image-object' key={img.media_id}>
              <ImgZoom src={img.media_id} alt={img.subtext || img.img_name} className={'reunion-gallery-image'} img_array={images}/>
              {/* <div className="reunion-gallery-image-subtext">
                {img.subtext || img.img_name}
              </div> */}
            </div>
          ))}
          {images.length === 0 &&
            <div className='no-files'>No images in gallery</div>
          }
        </div>
        {showArrows && 
          <div>
            <button className={`scroll-button back-button ${atLeft?"disabled-scroll":""}`} onClick={scrollLeft}>
              {'\u2039'}
            </button>
            <button className={`scroll-button next-button ${atRight?"disabled-scroll":""}`} onClick={scrollRight}>
              {'\u203A'}
            </button>
          </div>
        }
        {/* <button>expand box</button> */}
      </div>
    </div>
  )
}

function smoothScrollBy(element, distance, duration = 600) {
  if (!element) return;

  const start = element.scrollLeft;
  const startTime = performance.now();

  function easeInOut(t) {
    return t < 0.5
      ? 2 * t * t
      : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }

  function animate(time) {
    const elapsed = time - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const eased = easeInOut(progress);

    element.scrollLeft = start + distance * eased;

    if (progress < 1) requestAnimationFrame(animate);
  }

  requestAnimationFrame(animate);
}

function getNumberWithSuffix(num) {
  if (num >= 11 && num <= 13) {
    return `${num}th`;
  }

  switch (num % 10) {
    case 1: return `${num}st`;
    case 2: return `${num}nd`;
    case 3: return `${num}rd`;
    default: return `${num}th`;
  }
}

const parseReunionBio = (markdown) => {
  const sections = {
    overview: "",
    bestMoments: "",
    auction: "",
  };

  if (!markdown) return sections;

  const lines = markdown.split("\n");

  let currentSection = null;

  lines.forEach((line) => {
    const header = line.trim().toLowerCase();

    if (header === "## overview") {
      currentSection = "overview";
    } else if (header === "## best moments") {
      currentSection = "bestMoments";
    } else if (header === "## auction") {
      currentSection = "auction";
    } else if (currentSection) {
      sections[currentSection] += line + "\n";
    }
  });

  return sections;
};

function MarkdownContent ({ children, image_infos }) {
  return (
    <div className='reunion-md-container'>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => {
            return <div className="bio-image-paragraph">{children}</div>;
          },

          img: ({ src, alt }) => {
            if (
              src === undefined ||
              src === "" ||
              !src ||
              src === "%22undefined%22"
            ) {
              return <div className='bio-image-box'>Empty Image Box</div>;
            }

            return (
              <div className='bio-image-box'>
                <ImgZoom
                  src={src}
                  alt={alt}
                  className='bio-image'
                  bioPic={true}
                />

                {image_infos[src]?.subtext && (
                  <div className='bio-img-subtext'>{image_infos[src].subtext}</div>
                )}
              </div>
            );
          },
        }}
      >
        {children || ""}
      </ReactMarkdown>
    </div>
  );
}